import { SalesforcePromotionCountdown } from '~/lib/salesforce/types';
import { Typography } from '~/shared/components';
import { useCountdown, usePromotionCountdown } from '~/shared/hooks/countdown';
import { useUtils } from '~/shared/hooks/useUtils';
import { formatTimeLeftRemaining } from '~/shared/utils/format-time-left';
import { useTranslation } from '~/shared/utils/translation';
import styles from './BasketItemPromotionCountdown.module.scss';

type Props = {
    promotions?: SalesforcePromotionCountdown[];
};

/**
 * Tells the customer the line's promotion has not started yet, so its discount is missing from
 * the price. The basket is refreshed at zero by `BasketList`, once for every line.
 */
export const BasketItemPromotionCountdown = ({ promotions }: Props) => {
    const promotion = usePromotionCountdown(promotions);
    const endDate = promotion?.countdown.countdownEndDate;

    if (!promotion || !endDate) return null;

    return <CountdownBar endDate={endDate} upcomingDiscount={promotion.upcomingDiscount} />;
};

const CountdownBar = ({
    endDate,
    upcomingDiscount,
}: {
    endDate: string;
    upcomingDiscount?: number | null;
}) => {
    const { translate } = useTranslation();
    const { convertPriceToLocale } = useUtils();
    const { timeLeft, hasMounted } = useCountdown({ endDate });

    /*
     * `useCountdown` returns 0 for every unit until the component has mounted, to match the
     * server render. Showing nothing until then avoids a flash of "Tilbuddet starter om 0 sekunder".
     */
    if (!hasMounted) return null;

    const time = formatTimeLeftRemaining(timeLeft, { maxUnits: 2 });
    const amount = upcomingDiscount ? convertPriceToLocale(upcomingDiscount) : null;

    return (
        <div className={styles.basketItemPromotionCountdown}>
            <Typography variant="paragraph-S" weight="medium" as="p">
                {translate('basket.promotionCountdown.startsIn', { time }) ||
                    `Tilbuddet starter om ${time}`}
            </Typography>

            {!!amount && (
                <Typography variant="paragraph-S" as="p">
                    {translate('basket.promotionCountdown.upcomingDiscount', {
                        amount,
                    }) || `Du sparer ${amount} kr., når tilbuddet starter`}
                </Typography>
            )}
        </div>
    );
};
